import { useMemo } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { HttpClientError, IDefaultResponseMessage, IQuerySettings, QueryKeys, getQuerySettings } from '@dw/models/shared';
import { useAuth, useHttpClient, useSnackbar } from '@dw/hooks';
import { ICreateTeacherNoteReplyRequest, ICreateTeacherNoteRequest, IGetTeacherNoteAttachmentDownloadLinkRequest, IMarkTeacherNoteAsSeenRequest, IMyTeacherNote, ITeacherNote, ITeacherNoteReply, ITeacherNoteSeenResult, ITeacherNoteThreadSummary, IUnifiedTeacherNote, TEACHER_NOTE_REPLY_VISIBILITY } from "@dw/models/teacherNote";
import { IPreSignedBlobUrl } from "@dw/models/blob";
import { DATE_FORMATS } from "@dw/constants";
import { dayjs, Dayjs } from '@dw/dayjs';
import { hasExplicitTeacherNoteDateFilter, ITeacherNoteListFilters, mergeTeacherNotes } from '@dw/utils';

const BASE_URL = '/v1/teacher-notes';

export interface ITeacherNoteFilters {
  /** Bounds by note CREATION date. */
  fromDate?: Dayjs;
  /** Bounds by note CREATION date. */
  toDate?: Dayjs;
  /**
   * Bounds by last ACTIVITY date, applied before hydration. Use this for the default window so an
   * old note with a fresh reply still comes back; `fromDate` would wrongly exclude it.
   */
  activeSince?: Dayjs;
}

export const useGetTeacherNotes = (
  filters?: ITeacherNoteFilters,
  settings?: IQuerySettings<ITeacherNote[]>
) => {
  const { GET } = useHttpClient();

  const fromDate = filters?.fromDate?.format(DATE_FORMATS.YearMonthDayIso);
  const toDate = filters?.toDate?.format(DATE_FORMATS.YearMonthDayIso);
  const activeSince = filters?.activeSince?.format(DATE_FORMATS.YearMonthDayIso);

  const querySettings = getQuerySettings(settings);
  return useQuery<ITeacherNote[], HttpClientError>({
    queryKey: [QueryKeys.TeacherNotes, { fromDate, toDate, activeSince }],
    queryFn: () => {
      return GET<ITeacherNote[]>(`${BASE_URL}`, {
        fromDate,
        toDate,
        activeSince
      });
    },
    ...querySettings,
  });
};

export const useGetMyTeacherNotes = (
  settings?: IQuerySettings<IMyTeacherNote[]>
) => {
  const { GET } = useHttpClient();

  const querySettings = getQuerySettings(settings);
  return useQuery<IMyTeacherNote[], HttpClientError>({
    queryKey: [QueryKeys.MyTeacherNotes],
    queryFn: () => {
      return GET<IMyTeacherNote[]>(`${BASE_URL}/my`);
    },
    ...querySettings,
  });
};

/**
 * How far back the default window looks, bounded so neither the badge nor the notes rail pulls a
 * school's full history. Applied as `activeSince` (last-activity), so a months-old note with a
 * reply this week still surfaces.
 */
export const UNREAD_TEACHER_NOTES_LOOKBACK_DAYS = 30;

/**
 * How often the notes rail and the unread badges refresh themselves.
 *
 * React Query's `refetchIntervalInBackground` is left at its default of `false`, so a hidden or
 * backgrounded tab stops polling until the user returns to it.
 */
export const TEACHER_NOTES_POLL_INTERVAL_MS = 60_000;

/**
 * Everything a live-refreshing query needs here — `refetchInterval` alone is not enough:
 *
 * - `staleTime` overrides the app-wide 60-minute default, which would otherwise treat the data as
 *   fresh so that mounting the screen never revalidates and the pane renders hour-old receipts.
 *   Half the poll interval rather than 0: it still revalidates on mount and focus, but puts a
 *   floor under that traffic so alt-tabbing or bouncing between dashboard pages cannot fire a
 *   round-trip per navigation. The 60s interval remains the authoritative refresh.
 * - `refetchOnWindowFocus` overrides the app-wide `false`, so coming back to the tab catches up
 *   immediately instead of waiting out the remainder of the interval.
 *
 * Mirrors the settings the app-settings poller uses (`AppSettings.context.tsx`).
 */
const POLLED_NOTES_QUERY_SETTINGS = {
  refetchInterval: TEACHER_NOTES_POLL_INTERVAL_MS,
  refetchOnWindowFocus: true,
  staleTime: TEACHER_NOTES_POLL_INTERVAL_MS / 2,
};

/**
 * The received + authored query pair merged into one recency-ordered conversation list — the
 * single source of how the two representations combine.
 *
 * Owns the default 30-day window as `activeSince` (last-activity, so an old note with a fresh reply
 * still comes back), day-truncated so it formats to the same `YYYY-MM-DD` on every render within a
 * day: the query key stays stable (rolling over naturally at midnight) and the badge and the notes
 * view share one cache entry. `mergeTeacherNotes` dedupes by noteId with the recipient
 * representation winning, so an admin who also received a note counts it once.
 *
 * An explicit date search switches to `fromDate`/`toDate` (creation semantics) and drops the
 * default activity window, so the user can reach notes older than 30 days.
 *
 * Both queries poll on {@link TEACHER_NOTES_POLL_INTERVAL_MS}. Since this hook is the only owner
 * of the pair — the notes view and the unread badges all read through it — new notes and replies
 * surface everywhere in the portal on the same single interval, with no duplicate timers.
 */
export const useUnifiedTeacherNotes = (filters?: ITeacherNoteListFilters) => {
  const { user } = useAuth();

  const defaultActiveSince = dayjs().startOf('day').subtract(UNREAD_TEACHER_NOTES_LOOKBACK_DAYS, 'day');
  const hasExplicitDates = hasExplicitTeacherNoteDateFilter(filters);

  const { data: receivedNotes, isLoading: receivedLoading } = useGetMyTeacherNotes(POLLED_NOTES_QUERY_SETTINGS);
  const { data: authoredNotes, isLoading: authoredLoading } = useGetTeacherNotes({
    fromDate: filters?.fromDate ?? undefined,
    toDate: filters?.toDate ?? undefined,
    activeSince: hasExplicitDates ? undefined : defaultActiveSince,
  }, POLLED_NOTES_QUERY_SETTINGS);

  const notes = useMemo<IUnifiedTeacherNote[]>(
    () => mergeTeacherNotes(receivedNotes ?? [], authoredNotes ?? [], user?.id),
    [receivedNotes, authoredNotes, user?.id]
  );

  return { notes, isLoading: receivedLoading || authoredLoading };
};

/**
 * Count of unread ITEMS needing the current user's attention — badges the Communication tab and the
 * side-nav item.
 *
 * Counts items, not notes: each unread reply counts, plus one for a note never opened. Two notes
 * carrying four unread replies each therefore badge as 8, matching how a chat app counts messages
 * rather than conversations. Covers both directions for every portal role, counting each note once
 * by deriving from the same merged list the notes view renders.
 */
export const useUnreadTeacherNotesCount = (): number => {
  const { notes } = useUnifiedTeacherNotes();
  return useMemo(
    () => notes.reduce((total, note) => total + note.unreadReplyCount + (note.isNoteUnread ? 1 : 0), 0),
    [notes]
  );
};

/**
 * Create a new note addressed to one or more recipients.
 *
 * Any staff member may address any other — the server checks only that each recipient
 * belongs to this school, and that none of them is the caller. (There was a role-direction
 * rule until 2026-08: Owner/Admin could only write to Teachers and vice versa.)
 */
export const useCreateTeacherNoteMutation = (
  settings?: IQuerySettings<ITeacherNote, ICreateTeacherNoteRequest>
) => {
  const { POST } = useHttpClient();
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const createTeacherNote = async (request: ICreateTeacherNoteRequest) => {
    return POST<ITeacherNote>(`${BASE_URL}`, request);
  };

  const querySettings = getQuerySettings(settings);
  return useMutation<ITeacherNote, HttpClientError, ICreateTeacherNoteRequest>({
    mutationFn: createTeacherNote,
    ...querySettings,
    onSuccess: (response, input) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.TeacherNotes]
      });
      showSnackbar('Note successfully created');

      if (querySettings.onSuccess) {
        querySettings.onSuccess(response, input);
      }
    }
  });
};

/**
 * The one place the note-level unread flag is derived from its two components, so every cache patch
 * agrees with what the server would compute.
 */
const deriveHasUnreadActivity = (isNoteUnread: boolean, unreadReplyCount: number): boolean =>
  isNoteUnread || unreadReplyCount > 0;

/**
 * The thread a reply belongs to. A broadcast carries no `recipientUserId`, and neither does the
 * shared `Everyone` summary, so the same comparison resolves both kinds.
 */
const isReplyThread = (thread: ITeacherNoteThreadSummary, reply: ITeacherNoteReply): boolean =>
  thread.recipientUserId === reply.recipientUserId;

/**
 * Fold a newly posted reply into the note's thread rollups so the counts do not sit stale until the
 * next poll. `unreadReplyCount` is deliberately untouched: the poster has by definition seen their
 * own reply. A thread with no existing summary (the first reply in a conversation the server has
 * not summarised yet) is left for the poll to add — inventing one here would mean guessing its
 * display name.
 */
const applyReplyToThreads = (threads: ITeacherNoteThreadSummary[], reply: ITeacherNoteReply) =>
  threads.map(thread => isReplyThread(thread, reply)
    ? { ...thread, replyCount: thread.replyCount + 1, lastActivityTimestamp: reply.createdTimestamp }
    : thread);

/**
 * Drop a deleted reply from its thread rollup. Only `replyCount` moves — whether the deleted reply
 * was still unread for THIS caller is not knowable client-side, so `unreadReplyCount` is left for
 * the next poll to reconcile rather than guessed at.
 */
const removeReplyFromThreads = (threads: ITeacherNoteThreadSummary[], reply: ITeacherNoteReply) =>
  threads.map(thread => isReplyThread(thread, reply)
    ? { ...thread, replyCount: Math.max(0, thread.replyCount - 1) }
    : thread);

/**
 * Mark a note seen (fired silently when the note is opened) — advances the caller's seen-cursor so
 * the note body and the replies they have now viewed stop counting as unread.
 *
 * Omitting `visibility`/`recipientUserId` marks the note AND all the caller's threads seen; pass
 * them to narrow to one conversation. Both go on the query string, matching how the other
 * body-less POSTs in this codebase pass parameters (`buildUrl`).
 *
 * The response carries the recomputed unread state, so both caches are patched in place — the
 * received list on its stable key, every filter-keyed authored list via `setQueriesData` — and
 * opening a note triggers no refetch.
 */
export const useMarkTeacherNoteAsReadMutation = (
  settings?: IQuerySettings<ITeacherNoteSeenResult, IMarkTeacherNoteAsSeenRequest>
) => {
  const { POST, buildUrl } = useHttpClient();
  const queryClient = useQueryClient();

  const markTeacherNoteAsRead = async ({ noteId, visibility, recipientUserId }: IMarkTeacherNoteAsSeenRequest) => {
    return POST<ITeacherNoteSeenResult>(buildUrl(`${BASE_URL}/${noteId}/read`, { visibility, recipientUserId }));
  };

  const querySettings = getQuerySettings(settings);
  return useMutation<ITeacherNoteSeenResult, HttpClientError, IMarkTeacherNoteAsSeenRequest>({
    mutationFn: markTeacherNoteAsRead,
    ...querySettings,
    onSuccess: (response, input) => {
      const seenState = {
        threads: response.threads,
        isNoteUnread: response.isNoteUnread,
        unreadReplyCount: response.unreadReplyCount,
        hasUnreadActivity: deriveHasUnreadActivity(response.isNoteUnread, response.unreadReplyCount),
      };

      queryClient.setQueryData<IMyTeacherNote[]>(
        [QueryKeys.MyTeacherNotes],
        (existing) => existing?.map(note => note.noteId === response.noteId
          ? { ...note, ...seenState, readTimestamp: response.readTimestamp ?? note.readTimestamp }
          : note)
      );
      queryClient.setQueriesData<ITeacherNote[]>(
        { queryKey: [QueryKeys.TeacherNotes] },
        (existing) => existing?.map(note => note.noteId === response.noteId
          ? { ...note, ...seenState }
          : note)
      );

      if (querySettings.onSuccess) {
        querySettings.onSuccess(response, input);
      }
    }
  });
};

/**
 * Post a reply to an existing note. Both sides of a conversation can reply;
 * server-side authorization allows the note's creator, any recipient, or an
 * Owner/Admin to reply.
 *
 * Replies live in per-recipient private threads. Pass `recipientUserId` to
 * target a specific thread: a recipient replying in their own thread may omit
 * it (the server infers it), but the note author or an Owner/Admin posting into
 * someone's thread MUST supply it (a recipient of the note). When provided it is
 * forwarded in the request body.
 *
 * On success the returned reply is appended to the cached note in BOTH lists — the received list on
 * its stable key and every filter-keyed authored list via `setQueriesData` — so sending a reply
 * triggers no refetch. Posting also advances the poster's own seen-cursor server-side (their own
 * reply must never make the note unread for them), so the patch clears `hasUnreadActivity` and
 * bumps `lastActivityTimestamp` to keep the recency ordering correct.
 */
export const useCreateTeacherNoteReplyMutation = (
  settings?: IQuerySettings<ITeacherNoteReply, ICreateTeacherNoteReplyRequest>
) => {
  const { POST } = useHttpClient();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const createTeacherNoteReply = async ({ noteId, ...request }: ICreateTeacherNoteReplyRequest) => {
    return POST<ITeacherNoteReply>(`${BASE_URL}/${noteId}/replies`, request);
  };

  const querySettings = getQuerySettings(settings);
  return useMutation<ITeacherNoteReply, HttpClientError, ICreateTeacherNoteReplyRequest>({
    mutationFn: createTeacherNoteReply,
    ...querySettings,
    onSuccess: (response, input) => {
      // The received list only ever holds the caller's OWN thread plus broadcasts, so a Private
      // reply posted into someone else's thread (author/admin replying) must not be appended
      // there. An `Everyone` reply belongs to no single thread and is visible to all recipients,
      // so it always lands — including in the poster's own copy.
      const belongsToMyThread = response.visibility === TEACHER_NOTE_REPLY_VISIBILITY.EVERYONE
        || (!!response.recipientUserId && response.recipientUserId === user?.id);

      const applyReply = <T extends { noteId: string; replies: ITeacherNoteReply[]; threads: ITeacherNoteThreadSummary[] }>(notes?: T[]) =>
        notes?.map(note => {
          if (note.noteId !== input.noteId) {
            return note;
          }

          // Posting advances the poster's seen-cursor for the thread they posted INTO — and for the
          // note body — but leaves their other threads untouched. Zero only that thread, then
          // recompute the note-level total from the rollups instead of assuming the note is read;
          // assuming it made the nav badge under-report until the next poll.
          const threads = applyReplyToThreads(note.threads, response)
            .map(thread => isReplyThread(thread, response) ? { ...thread, unreadReplyCount: 0 } : thread);
          const unreadReplyCount = threads.reduce((total, thread) => total + thread.unreadReplyCount, 0);

          return {
            ...note,
            replies: [...note.replies, response],
            threads,
            isNoteUnread: false,
            unreadReplyCount,
            hasUnreadActivity: deriveHasUnreadActivity(false, unreadReplyCount),
            lastActivityTimestamp: response.createdTimestamp,
          };
        });

      if (belongsToMyThread) {
        queryClient.setQueryData<IMyTeacherNote[]>([QueryKeys.MyTeacherNotes], applyReply);
      }
      queryClient.setQueriesData<ITeacherNote[]>({ queryKey: [QueryKeys.TeacherNotes] }, applyReply);

      if (querySettings.onSuccess) {
        querySettings.onSuccess(response, input);
      }
    }
  });
};

export interface IDeleteTeacherNoteReplyRequest {
  noteId: string;
  replyId: string;
}

/**
 * Delete a single reply. Server-side authorization allows the reply's author OR any Owner/Admin.
 *
 * Both caches are patched in place (the received list on its stable key, every filter-keyed
 * authored list via `setQueriesData`) so the bubble disappears without a refetch — which would
 * otherwise remount the detail pane and lose the composer's draft and scroll position.
 */
export const useDeleteTeacherNoteReplyMutation = (
  settings?: IQuerySettings<IDefaultResponseMessage, IDeleteTeacherNoteReplyRequest>
) => {
  const { DELETE } = useHttpClient();
  const queryClient = useQueryClient();

  const deleteTeacherNoteReply = async ({ noteId, replyId }: IDeleteTeacherNoteReplyRequest) => {
    return DELETE<IDefaultResponseMessage>(`${BASE_URL}/${noteId}/replies/${replyId}`);
  };

  const querySettings = getQuerySettings(settings);
  return useMutation<IDefaultResponseMessage, HttpClientError, IDeleteTeacherNoteReplyRequest>({
    mutationFn: deleteTeacherNoteReply,
    ...querySettings,
    onSuccess: (response, input) => {
      const removeReply = <T extends { noteId: string; replies: ITeacherNoteReply[]; threads: ITeacherNoteThreadSummary[] }>(notes?: T[]) =>
        notes?.map(note => {
          if (note.noteId !== input.noteId) {
            return note;
          }

          // Look the reply up before dropping it — its thread is only knowable from the reply.
          const removed = note.replies.find(reply => reply.replyId === input.replyId);
          return {
            ...note,
            replies: note.replies.filter(reply => reply.replyId !== input.replyId),
            threads: removed ? removeReplyFromThreads(note.threads, removed) : note.threads,
          };
        });

      queryClient.setQueryData<IMyTeacherNote[]>([QueryKeys.MyTeacherNotes], removeReply);
      queryClient.setQueriesData<ITeacherNote[]>({ queryKey: [QueryKeys.TeacherNotes] }, removeReply);

      if (querySettings.onSuccess) {
        querySettings.onSuccess(response, input);
      }
    }
  });
};

/**
 * Delete a note. Server-side authorization allows deletion by the note's
 * creator OR any Owner/Admin at the dance school.
 */
export const useDeleteTeacherNoteMutation = (
  settings?: IQuerySettings<IDefaultResponseMessage, string>
) => {
  const { DELETE } = useHttpClient();
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const deleteTeacherNote = async (noteId: string) => {
    return DELETE<IDefaultResponseMessage>(`${BASE_URL}/${noteId}`);
  };

  const querySettings = getQuerySettings(settings);
  return useMutation<IDefaultResponseMessage, HttpClientError, string>({
    mutationFn: deleteTeacherNote,
    ...querySettings,
    onSuccess: (response, input) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.TeacherNotes]
      });
      // An admin can delete a note they received, so the received list must drop it too.
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.MyTeacherNotes]
      });
      showSnackbar('Note successfully deleted');

      if (querySettings.onSuccess) {
        querySettings.onSuccess(response, input);
      }
    }
  });
};

export interface IRemoveTeacherNoteRecipientRequest {
  noteId: string;
  userId: string;
}

/**
 * Remove a recipient from a note, revoking their access to it.
 *
 * Unlike the other mutations here this INVALIDATES both lists rather than patching them. Removing a
 * recipient changes the note's `recipients` AND may drop that recipient's whole private-thread
 * conversation from `replies`, so a surgical patch would have to replicate the server's thread
 * rules to stay correct. This is a rare admin action with no draft state to preserve, so the
 * refetch is the cheaper and safer trade — a deliberate deviation from the patch-first convention
 * the rest of this file follows.
 */
export const useRemoveTeacherNoteRecipientMutation = (
  settings?: IQuerySettings<IDefaultResponseMessage, IRemoveTeacherNoteRecipientRequest>
) => {
  const { DELETE } = useHttpClient();
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const removeTeacherNoteRecipient = async ({ noteId, userId }: IRemoveTeacherNoteRecipientRequest) => {
    return DELETE<IDefaultResponseMessage>(`${BASE_URL}/${noteId}/recipients/${userId}`);
  };

  const querySettings = getQuerySettings(settings);
  return useMutation<IDefaultResponseMessage, HttpClientError, IRemoveTeacherNoteRecipientRequest>({
    mutationFn: removeTeacherNoteRecipient,
    ...querySettings,
    onSuccess: (response, input) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.TeacherNotes]
      });
      // The CALLER may be an admin-recipient whose own received copy carries the removed
      // person's thread - refresh it too. (The removed user's browser refreshes via its own poll.)
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.MyTeacherNotes]
      });
      showSnackbar('Recipient successfully removed');

      if (querySettings.onSuccess) {
        querySettings.onSuccess(response, input);
      }
    }
  });
};

export const useGetTeacherNoteAttachmentDownloadLinkMutation = (
  settings?: IQuerySettings<IPreSignedBlobUrl, IGetTeacherNoteAttachmentDownloadLinkRequest>
) => {
  const { GET } = useHttpClient();

  const getTeacherNoteAttachmentDownloadLink = async ({ noteId, attachmentKey }: IGetTeacherNoteAttachmentDownloadLinkRequest) => {
    return GET<IPreSignedBlobUrl>(`${BASE_URL}/${noteId}/download-link`, { attachmentKey });
  };

  const querySettings = getQuerySettings(settings);
  return useMutation<IPreSignedBlobUrl, HttpClientError, IGetTeacherNoteAttachmentDownloadLinkRequest>({
    mutationFn: getTeacherNoteAttachmentDownloadLink,
    ...querySettings,
  });
};
